import { GetApiIntroducerPortalIntroducerApplicationDeclineReasonResponse } from '@iwoca/lapi-client/introducer_portal';
import { t } from '@lingui/macro';
import { getDefaultRejectReason } from 'components/RejectedOffer/useRejectReason.helpers';
import { TIntroducerApplication } from 'lendingApi/applications.types';
import { TStepType } from 'types';
import { APPLICATION_STATUS } from '../applications/applications.config';

const getNumberOfOfferColumns = ({
  formal_offer_sets,
  requested_products,
}: TIntroducerApplication) => {
  const formalOffers = formal_offer_sets?.[0]?.formal_offers;

  if (formalOffers?.length > 0) {
    if (formalOffers?.find((item) => item.is_selected)) {
      return 1;
    } else {
      return formalOffers.length;
    }
  }
  return requested_products.length;
};

type TGetApplicationStepData = {
  application: TIntroducerApplication;
  rejectReason?: string;
  declineReasons?: GetApiIntroducerPortalIntroducerApplicationDeclineReasonResponse['data'];
};

export const getApplicationStepData = ({
  application,
  rejectReason,
  declineReasons,
}: TGetApplicationStepData): TStepType => {
  const { formal_offer_sets, status, customer, is_partner_channel } =
    application;

  const offers = formal_offer_sets[0]?.formal_offers;
  const amountOfOffers = offers?.length || 0;
  const multipleOffers =
    amountOfOffers > 1 && !offers?.find((item) => item.is_selected);

  const renderDeclineSubheading = (defaultMessage: string) => {
    const hasDeclineReasons = declineReasons && declineReasons?.length > 0;
    const hasMultipleDeclineReasons =
      declineReasons && declineReasons.length > 1;

    if (!hasDeclineReasons) {
      return defaultMessage;
    }

    return (
      <>
        <p>We are unable to proceed with your application at this time.</p>
        <span>
          {hasMultipleDeclineReasons ? 'Reasons' : 'Reason'} for decline:
        </span>
        <ul>
          {declineReasons.map((declineReason) => {
            return <li key={declineReason.reason}>{declineReason.reason}</li>;
          })}
        </ul>
      </>
    );
  };

  if (is_partner_channel) {
    switch (status) {
      case APPLICATION_STATUS.open:
        return {
          heading: t`Continue application`,
          subHeading: "The customer's application is still in progress.",
        };
      case APPLICATION_STATUS.action_required:
        return {
          heading: 'Documents required',
          subHeading: 'More documents are required from the customer.',
        };
      case APPLICATION_STATUS.declined:
        return {
          heading: 'Application declined',
          subHeading: renderDeclineSubheading(
            'The application has been declined.',
          ),
        };
      case APPLICATION_STATUS.expired:
        return {
          heading: multipleOffers ? 'Offers expired' : 'Offer expired',
          subHeading: multipleOffers
            ? 'The original offers have expired, but the customer can log in and refresh their offers for an updated decision. Additional documents may be required.'
            : 'The original offer has expired, but the customer can log in and refresh their offer for an updated decision. Additional documents may be required.',
        };
      case APPLICATION_STATUS.offered:
        return {
          heading: multipleOffers ? 'Review offers' : 'Review offer',
          subHeading: multipleOffers
            ? 'The customer has received these offers.'
            : 'The customer has received this offer.',
        };
      case APPLICATION_STATUS.talking_to_customer:
        return {
          heading: 'Applicant funding steps',
          subHeading:
            'The applicant just needs to complete the following funding steps to access their funds.',
        };
    }
  }

  switch (status) {
    case APPLICATION_STATUS.open: {
      if (customer.date_sign_up_finished) {
        return {
          heading: t`Documents required`,
          subHeading: t`To complete this application, please upload the required documents and any additional supporting information in the documents tab.`,
          button: true,
        };
      } else {
        return {
          heading: t`Continue the application`,
          subHeading: t`Pick up where you left off.`,
          button: true,
        };
      }
    }
    case APPLICATION_STATUS.action_required: {
      return {
        heading: t`Documents required`,
        subHeading: t`To complete this application, please upload the required documents and any additional supporting information in the documents tab.`,
      };
    }
    case APPLICATION_STATUS.customer_to_link_open_banking: {
      return {
        heading: t`Link open banking`,
        subHeading: t`The application is with your client to link their trading bank account. Notify your client to link their trading account with open banking.`,
      };
    }
    case APPLICATION_STATUS.offer_revoked: {
      return {
        heading: amountOfOffers > 1 ? t`Offers revoked` : t`Offer revoked`,
        subHeading:
          amountOfOffers > 1
            ? t`Unfortunately, the offers for this application have been revoked.`
            : t`Unfortunately, the offer for this application has been revoked.`,
      };
    }
    case APPLICATION_STATUS.manual_review: {
      return {
        heading: t`Manual check`,
        subHeading: t`Our team needs to manually check details in the application before it can be reviewed. Please reach out to us if you have not heard from the team after 24 hours.`,
      };
    }
    case APPLICATION_STATUS.awaiting_underwriter: {
      return {
        heading: t`Awaiting underwriter`,
        subHeading: t`The application is complete and an underwriter will be assigned to review it soon.`,
      };
    }
    case APPLICATION_STATUS.in_underwriting: {
      return {
        heading: t`In review by underwriter`,
        subHeading: t`An underwriter is reviewing the application - we’ll send you an update about it soon.`,
      };
    }
    case APPLICATION_STATUS.declined: {
      return {
        heading: t`Application declined`,
        subHeading: renderDeclineSubheading(
          t`The application has been declined. Where possible, we’ll send details to you by email on why we’ve made this decision.`,
        ),
      };
    }
    case APPLICATION_STATUS.deferred: {
      return {
        heading: t`Application deferred`,
        subHeading: t`We’re currently not able to proceed with this application.`,
      };
    }
    case APPLICATION_STATUS.expired: {
      return {
        heading: t`Refresh`,
        subHeading: multipleOffers
          ? t`The original offers have expired, but you can resubmit the original application to review the applicant for an updated decision. Additional documents may be required.`
          : t`The original offer has expired, but you can resubmit the original application to review the applicant for an updated decision. Additional documents may be required.`,
      };
    }
    case APPLICATION_STATUS.conditional_offer: {
      return {
        heading:
          amountOfOffers > 1
            ? 'Review conditional offers'
            : 'Review conditional offer',
        subHeading:
          amountOfOffers > 1
            ? "These offers depend on reviewing the applicant's latest VAT return(s), and they may be withdrawn based on that information."
            : "This offer depends on reviewing the applicant's latest VAT return(s), and it may be withdrawn based on that information.",
      };
    }
    case APPLICATION_STATUS.offered: {
      const numberOfColumns = getNumberOfOfferColumns(application);

      if (numberOfColumns > 1) {
        return {
          heading:
            numberOfColumns === 2
              ? t`Congratulations, you have two approved offers!`
              : t`Congratulations, you have multiple approved offers!`,
          subHeading: t`Select one to see the early repayment plan below.`,
        };
      } else {
        return {
          heading: t`Congratulations, this is your approved offer!`,
          subHeading: t`You can see the early repayment plan below.`,
          link: t`Go to review offer tab`,
        };
      }
    }
    case APPLICATION_STATUS.offer_rejected: {
      return {
        heading: amountOfOffers > 1 ? t`Offers rejected` : t`Offer rejected`,
        subHeading: rejectReason || getDefaultRejectReason(amountOfOffers > 1),
      };
    }
    case APPLICATION_STATUS.talking_to_customer: {
      return {
        heading: t`Applicant funding steps`,
        subHeading: t`The applicant has accepted an offer and just needs to complete the following funding steps to access funds.`,
      };
    }
    case APPLICATION_STATUS.funded: {
      return {
        heading: t`Offer funded`,
        subHeading: t`Congratulations, the applicant has taken their funds.`,
      };
    }
    default:
      return {} as TStepType;
  }
};
